/**
 * Fully-offline mock for the procurement APIs.
 *
 * Enabled by REACT_APP_PROCUREMENT_MOCK=true (see .env.development.local).
 * No backend / network — in-memory data that lives for the browser session.
 * Mirrors the real oh-api response shapes AND the hard budget-cap behaviour so
 * you can see blocking work. Reset by refreshing the page.
 */

import catalogue from './procurementCatalogue.json'
import { DEFAULT_SOPS } from '../app/pages/Procurement/constants'

let idCounter = 1
const nextId = (p) => `${p}-${idCounter++}`

const centres = [
  { uuid: 'centre-indiranagar', name: 'Indiranagar' },
  { uuid: 'centre-koramangala', name: 'Koramangala' },
  { uuid: 'centre-hsr', name: 'HSR' }
]

// budgets: { uuid, centre, category, month, amount }
let budgets = []

// petty cash: { uuid, centre, flow, date, entry_type, amount, description,
//               created, created_by_name }
// Seeded so the ledger opens with rows to look at (offline mode has no backend
// to fetch history from). Two people file at this centre, which is what the
// "Entered by" column is there to show.
const thisMonth = new Date().toISOString().slice(0, 7)
let pettyCash = [
  ['petty_cash', '02', 'Travel', 'Auto to the bank', 220, 'Meera Iyer'],
  ['petty_cash', '04', 'Pantry', 'Milk and tea for the week', 640, 'Meera Iyer'],
  ['petty_cash', '05', 'Stationery', 'Chart paper, markers', 385, 'Rohit Nair'],
  ['repair_maintenance', '01', 'Plumbing', 'Washroom tap replaced', 1850, 'Rohit Nair'],
  ['repair_maintenance', '03', 'Electrical', 'Tube light — Room 2', 640, 'Meera Iyer'],
  ['repair_maintenance', '06', 'Carpentry', 'Cupboard hinge', 320, 'Rohit Nair']
].map(([flow, day, category, description, amount, who], i) => ({
  uuid: `pc-seed-${i + 1}`,
  centre: 'centre-indiranagar',
  flow,
  date: `${thisMonth}-${day}`,
  entry_type: 'spent',
  category,
  description,
  amount,
  invoice_count: flow === 'repair_maintenance' && day !== '06' ? 1 : 0,
  created: `${thisMonth}-${day}T10:00:00Z`,
  created_by_name: who
}))
// HQ allocations keyed by `${centre}|${month}` -> { amount, note }
const pettyCashAllocations = {}
// per-centre/month state keyed by `${centre}|${month}` -> { frozen, acknowledged }
const pettyCashMonths = {}
const pcMonthKey = (centre, month) => `${centre}|${month.slice(0, 7)}-01`
const isMonthFrozen = (centre, month) =>
  !!pettyCashMonths[pcMonthKey(centre, month)]?.frozen

// consumable roster items (centre=null => global)
const consumableItems = [
  { uuid: 'ci-a4', centre: null, name: 'A4 Paper', unit: 'ream', default_price: 250, is_active: true },
  { uuid: 'ci-marker', centre: null, name: 'Whiteboard Marker', unit: 'pc', default_price: 40, is_active: true },
  { uuid: 'ci-glue', centre: null, name: 'Glue Stick', unit: 'pc', default_price: 30, is_active: true },
  { uuid: 'ci-crayon', centre: null, name: 'Crayon Box', unit: 'box', default_price: 90, is_active: true }
]
// consumable orders keyed by `${centre}|${month}` -> { [itemId]: { qty, unit_price } }
const consumableOrders = {}
// frozen "original" consumables list keyed by `${centre}|${month}` -> [rows]
const consumableSnapshots = {}
// Submit lifecycle per centre|month: { status, po_number, submitted_at }
const consumableMonths = {}

// program material orders keyed by `${centre}|${month}`
// -> { status, lines:[], po_number, review_remarks, attachments:[] }
const pmOrders = {}

// notifications (newest first)
let notifications = []

const centreName = (centre) =>
  centres.find((c) => c.uuid === centre)?.name || centre

const centreCode = (centre) =>
  (centreName(centre).replace(/[^A-Za-z]/g, '').slice(0, 3) || 'CEN').toUpperCase()

const generatePo = (centre, month) =>
  `${centreCode(centre)}/${month.slice(0, 7)}/01`

const pushNotification = (n) =>
  notifications.unshift({
    id: nextId('ntf'),
    read: false,
    created: new Date().toISOString(),
    ...n
  })

const delay = (value) => new Promise((res) => setTimeout(() => res(value), 150))
const fail = (message) => {
  // mirrors how the real request layer surfaces backend errors
  return Promise.reject(new Error(message))
}

const findBudget = (centre, category, month) =>
  budgets.find(
    (b) => b.centre === centre && b.category === category && b.month === month
  )

const budgetAmount = (centre, category, month) => {
  const b = findBudget(centre, category, month)
  return b ? Number(b.amount) : null
}

const usedAmount = (centre, category, month) => {
  if (category === 'petty_cash') {
    return pettyCash
      .filter(
        (e) =>
          e.centre === centre &&
          e.entry_type === 'spent' &&
          e.date.slice(0, 7) === month.slice(0, 7)
      )
      .reduce((s, e) => s + Number(e.amount), 0)
  }
  if (category === 'consumables') {
    const map = consumableOrders[`${centre}|${month}`] || {}
    return Object.values(map).reduce(
      (s, r) => s + Number(r.qty) * Number(r.unit_price),
      0
    )
  }
  if (category === 'program_material') {
    const order = pmOrders[`${centre}|${month}`]
    if (!order || !['submitted', 'approved'].includes(order.status)) return 0
    return order.lines.reduce(
      (s, l) => s + Number(l.qty) * Number(l.unit_cost),
      0
    )
  }
  return 0
}

// ---- Carry-forward ledger (mirrors dashboard src/lib/carry-forward.ts) ----
const CARRY_FORWARD_MONTHS = 3

function computeCarryForward(series) {
  const sorted = [...series].sort((a, b) => a.month.localeCompare(b.month))
  let debt = 0
  let monthsInDebt = 0
  const out = []
  for (const { month, base, actual } of sorted) {
    let wroteOff = 0
    if (monthsInDebt > CARRY_FORWARD_MONTHS) {
      wroteOff = debt
      debt = 0
      monthsInDebt = 0
    }
    const carry = debt
    const final = base - carry
    const variance = final - actual
    out.push({ month, base, carry, final, actual, variance, wroteOff })
    if (actual > final) {
      debt = actual - final
      monthsInDebt += 1
    } else {
      debt = 0
      monthsInDebt = 0
    }
  }
  return out
}

const monthAdd = (monthIso, n) => {
  const [y, m] = monthIso.split('-').map(Number)
  const total = (y * 12 + (m - 1)) + n
  const ny = Math.floor(total / 12)
  const nm = (total % 12) + 1
  return `${ny}-${String(nm).padStart(2, '0')}-01`
}

const monthsRange = (start, end) => {
  const out = []
  let cur = start
  // guard against runaway loops
  for (let i = 0; i < 60 && cur <= end; i++) {
    out.push(cur)
    cur = monthAdd(cur, 1)
  }
  return out
}

const DASHBOARD_CATEGORIES = [
  { key: 'program_material', name: 'Program Material' },
  { key: 'consumables', name: 'Consumables' },
  { key: 'petty_cash', name: 'Petty Cash' }
]

const fiscalYearLabel = (monthIso) => {
  const [y, m] = monthIso.split('-').map(Number)
  return m >= 4
    ? `${y}-${String((y + 1) % 100).padStart(2, '0')}`
    : `${y - 1}-${String(y % 100).padStart(2, '0')}`
}

function centreMonthsWithData(centre) {
  const months = new Set()
  budgets.filter((b) => b.centre === centre).forEach((b) => months.add(b.month))
  pettyCash
    .filter((e) => e.centre === centre)
    .forEach((e) => months.add(`${e.date.slice(0, 7)}-01`))
  Object.keys(consumableOrders).forEach((k) => {
    const [c, m] = k.split('|')
    if (c === centre) months.add(m)
  })
  Object.keys(pmOrders).forEach((k) => {
    const [c, m] = k.split('|')
    if (c === centre) months.add(m)
  })
  return months
}

export const fetchCentreDashboard = (queries = {}) => {
  const { centre, month } = queries
  if (!centre || !month) return delay({ categories: [], activity: [] })

  // Per-category figures (month budget vs month spend; no carry-forward for
  // these categories — the 3-month carry rule is R&M-only). Overspend is
  // allowed and surfaces as over / covered-by-pool.
  const cats = DASHBOARD_CATEGORIES.map((cat) => {
    const base = budgetAmount(centre, cat.key, month) || 0
    const utilised = Number(usedAmount(centre, cat.key, month) || 0)
    return {
      key: cat.key,
      name: cat.name,
      base,
      carry: 0,
      effective: base,
      utilised,
      balance: base - utilised
    }
  })

  const totalEffective = cats.reduce((s, c) => s + c.effective, 0)
  const totalUtilised = cats.reduce((s, c) => s + c.utilised, 0)
  const totalBalance = totalEffective - totalUtilised
  const centreOver = totalBalance < 0

  const withTone = cats.map((c) => ({
    ...c,
    tone: c.balance >= 0 ? 'ok' : centreOver ? 'over' : 'covered'
  }))

  // Recent activity for the selected month.
  const activity = []
  const pm = pmOrders[`${centre}|${month}`]
  if (pm && pm.lines.length) {
    const amt = pm.lines.reduce(
      (s, l) => s + Number(l.qty) * Number(l.unit_cost),
      0
    )
    activity.push({
      id: `pm-${month}`,
      category: 'Program Material',
      date: month,
      description: `Program Material order (${pm.lines.length} item${
        pm.lines.length > 1 ? 's' : ''
      })`,
      amount: amt,
      status: pm.status
    })
  }
  const consMap = consumableOrders[`${centre}|${month}`] || {}
  const consTotal = Object.values(consMap).reduce(
    (s, r) => s + Number(r.qty) * Number(r.unit_price),
    0
  )
  if (consTotal > 0) {
    activity.push({
      id: `cons-${month}`,
      category: 'Consumables',
      date: month,
      description: `Consumables (${Object.keys(consMap).length} items)`,
      amount: consTotal,
      status: null
    })
  }
  pettyCash
    .filter((e) => e.centre === centre && e.date.slice(0, 7) === month.slice(0, 7))
    .forEach((e) => {
      activity.push({
        id: `pc-${e.uuid}`,
        category: 'Petty Cash',
        date: e.date,
        description: e.description || e.entry_type,
        amount: e.amount,
        received: e.entry_type === 'received',
        status: null
      })
    })
  activity.sort((a, b) => (b.date > a.date ? 1 : -1))

  return delay({
    fiscalYear: fiscalYearLabel(month),
    pool: { totalEffective, totalUtilised, totalBalance, over: centreOver },
    categories: withTone,
    activity
  })
}

// ---- Centres ----
export const fetchCentres = () => delay({ results: centres })
export const fetchMyCentres = () => delay({ results: centres })

// ---- Budgets ----
export const fetchBudgets = (queries = {}) => {
  let rows = budgets
  if (queries.month) rows = rows.filter((b) => b.month === queries.month)
  const withCalc = rows.map((b) => {
    const used = usedAmount(b.centre, b.category, b.month)
    return {
      ...b,
      centre_name: centres.find((c) => c.uuid === b.centre)?.name,
      used,
      remaining: Number(b.amount) - used
    }
  })
  return delay({ results: withCalc })
}

export const createBudget = (data) => {
  const row = { uuid: nextId('budget'), ...data }
  budgets.push(row)
  return delay(row)
}

export const updateBudget = (uuid, data) => {
  budgets = budgets.map((b) => (b.uuid === uuid ? { ...b, ...data } : b))
  return delay(budgets.find((b) => b.uuid === uuid))
}

export const deleteBudget = (uuid) => {
  budgets = budgets.filter((b) => b.uuid !== uuid)
  return delay({})
}

export const transferBudget = (data) => {
  const { centre, month, from_category, to_category, amount } = data
  const amt = Number(amount)
  const find = (cat) =>
    budgets.find(
      (b) => b.centre === centre && b.month === month && b.category === cat
    )
  const source = find(from_category)
  if (!source || Number(source.amount) < amt) {
    return Promise.reject(
      new Error(`Only ${source ? source.amount : 0} budgeted for ${from_category}.`)
    )
  }
  source.amount = Number(source.amount) - amt
  let dest = find(to_category)
  if (!dest) {
    dest = { uuid: nextId('budget'), centre, month, category: to_category, amount: 0 }
    budgets.push(dest)
  }
  dest.amount = Number(dest.amount) + amt
  return delay({ from: source, to: dest })
}

// ---- Petty Cash ----
export const fetchPettyCash = (queries = {}) => {
  const flow = queries.flow || 'petty_cash'
  let rows = pettyCash.filter(
    (e) => e.centre === queries.centre && (e.flow || 'petty_cash') === flow
  )
  if (queries.month) {
    rows = rows.filter((e) => e.date.slice(0, 7) === queries.month.slice(0, 7))
  }
  return delay({ results: rows })
}

export const createPettyCash = (data) => {
  // Overspend is allowed (shown as over / covered-by-pool), not blocked.
  // Centre records only spends now.
  const row = {
    uuid: nextId('pc'),
    created: new Date().toISOString(),
    // The real API stamps request.user here.
    created_by_name: 'You',
    ...data,
    entry_type: 'spent'
  }
  pettyCash.push(row)
  return delay(row)
}

// Multi-line submission: several dated lines saved together (shared submission_id).
export const createPettyCashBatch = (data) => {
  const { centre, lines, flow } = data
  const submission = nextId('sub')
  const created = lines.map((l) => {
    const entry = {
      uuid: nextId('pc'),
      created: new Date().toISOString(),
      // The real API stamps request.user here.
      created_by_name: 'You',
      submission_id: submission,
      centre,
      flow: flow || 'petty_cash',
      date: l.date,
      entry_type: 'spent', // centre records only spends
      category: l.category || '',
      amount: Number(l.amount),
      description: l.description || ''
    }
    pettyCash.push(entry)
    return entry
  })
  return delay({ ok: true, count: created.length, results: created })
}

export const deletePettyCash = (uuid) => {
  pettyCash = pettyCash.filter((e) => e.uuid !== uuid)
  return delay({})
}

export const fetchPettyCashSummary = (queries = {}) => {
  const { centre, month } = queries
  const all = pettyCash.filter((e) => e.centre === centre)
  const received = all
    .filter((e) => e.entry_type === 'received')
    .reduce((s, e) => s + Number(e.amount), 0)
  const spent = all
    .filter((e) => e.entry_type === 'spent')
    .reduce((s, e) => s + Number(e.amount), 0)
  const allocations = Object.entries(pettyCashAllocations)
    .filter(([k]) => k.startsWith(`${centre}|`))
    .reduce((s, [, a]) => s + Number(a.amount || 0), 0)
  const monthAlloc = Number(
    pettyCashAllocations[pcMonthKey(centre, month)]?.amount || 0
  )
  const monthSpent = usedAmount(centre, 'petty_cash', month)
  const budget = budgetAmount(centre, 'petty_cash', month)
  const pcm = pettyCashMonths[pcMonthKey(centre, month)] || {}
  return delay({
    cash_in_hand: allocations + received - spent,
    month_spent: monthSpent,
    month_allocation: monthAlloc,
    budget,
    remaining: budget === null ? null : budget - monthSpent,
    frozen: !!pcm.frozen,
    acknowledged: !!pcm.acknowledged
  })
}

export const fetchPettyCashAllocations = (queries = {}) => {
  const { centre, month } = queries
  const results = Object.entries(pettyCashAllocations)
    .filter(([k]) => {
      const [c, m] = k.split('|')
      if (centre && c !== centre) return false
      if (month && m !== `${month.slice(0, 7)}-01`) return false
      return true
    })
    .map(([k, a]) => {
      const [c, m] = k.split('|')
      return {
        centre: c,
        centre_name: centreName(c),
        month: m,
        amount: a.amount,
        note: a.note || ''
      }
    })
  return delay({ results })
}

export const fetchPettyCashOverview = (month) => {
  const m = `${month.slice(0, 7)}-01`
  const results = centres.map((c) => {
    const allocAll = Object.entries(pettyCashAllocations)
      .filter(([k]) => k.startsWith(`${c.uuid}|`))
      .reduce((s, [, a]) => s + Number(a.amount || 0), 0)
    const spentAll = pettyCash
      .filter((e) => e.centre === c.uuid && e.entry_type === 'spent')
      .reduce((s, e) => s + Number(e.amount), 0)
    const receivedAll = pettyCash
      .filter((e) => e.centre === c.uuid && e.entry_type === 'received')
      .reduce((s, e) => s + Number(e.amount), 0)
    const monthSpent = pettyCash
      .filter(
        (e) =>
          e.centre === c.uuid &&
          e.entry_type === 'spent' &&
          e.date.slice(0, 7) === month.slice(0, 7)
      )
      .reduce((s, e) => s + Number(e.amount), 0)
    const pcm = pettyCashMonths[`${c.uuid}|${m}`] || {}
    return {
      centre: c.uuid,
      centre_name: c.name,
      allocation: Number(pettyCashAllocations[`${c.uuid}|${m}`]?.amount || 0),
      month_spent: monthSpent,
      cash_in_hand: allocAll + receivedAll - spentAll,
      frozen: !!pcm.frozen,
      acknowledged: !!pcm.acknowledged
    }
  })
  return delay({ results })
}

export const savePettyCashDraft = ({ lines }) => {
  const kept = (lines || []).filter((l) => Number(l.amount) > 0)
  const total = kept.reduce((s, l) => s + Number(l.amount), 0)
  return delay({ ok: true, total: String(total), lines: kept.length })
}

export const savePettyCashAllocation = (data) => {
  const { centre, month, amount, note } = data
  pettyCashAllocations[pcMonthKey(centre, month)] = {
    amount: Number(amount || 0),
    note: note || ''
  }
  return delay({ centre, month, amount: Number(amount || 0), note: note || '' })
}

export const acknowledgePettyCash = (centre, month) => {
  const key = pcMonthKey(centre, month)
  pettyCashMonths[key] = { ...(pettyCashMonths[key] || {}), acknowledged: true }
  return delay({ acknowledged: true })
}

export const setPettyCashFreeze = (centre, month, frozen = true) => {
  const key = pcMonthKey(centre, month)
  pettyCashMonths[key] = { ...(pettyCashMonths[key] || {}), frozen: !!frozen }
  return delay({ frozen: !!frozen })
}

export const unlockPettyCash = (centre, month, unlocked = true) => {
  const key = pcMonthKey(centre, month)
  pettyCashMonths[key] = { ...(pettyCashMonths[key] || {}), unlocked: !!unlocked }
  return delay({ unlocked: !!unlocked, frozen: false })
}

// ---- Consumables ----
export const fetchConsumableRoster = (queries = {}) => {
  const { centre, month } = queries
  const map = consumableOrders[`${centre}|${month}`] || {}
  const knownIds = new Set(consumableItems.map((i) => i.uuid))
  const rosterRows = consumableItems
    .filter((i) => i.is_active && (i.centre === null || i.centre === centre))
    .map((i) => {
      const o = map[i.uuid]
      const qty = o ? o.qty : 0
      const unit_price = o ? o.unit_price : i.default_price
      return {
        consumable_item: i.uuid,
        name: i.name,
        unit: i.unit,
        default_price: i.default_price,
        is_global: i.centre === null,
        is_adhoc: false,
        qty,
        unit_price,
        total: qty * unit_price
      }
    })
  // one-off items saved for this month (not in the roster master)
  const adhocRows = Object.entries(map)
    .filter(([id]) => !knownIds.has(id))
    .map(([id, o]) => ({
      consumable_item: id,
      name: o.name || 'One-off item',
      unit: o.unit || '',
      default_price: o.unit_price,
      is_global: false,
      is_adhoc: true,
      qty: o.qty,
      unit_price: o.unit_price,
      total: o.qty * o.unit_price
    }))
  const rows = [...rosterRows, ...adhocRows]
  const budget = budgetAmount(centre, 'consumables', month)
  const cm = consumableMonths[`${centre}|${month}`] || {}
  const submitted = cm.status === 'submitted'
  // Drafts don't count toward spend (mirrors the server).
  const used = submitted ? usedAmount(centre, 'consumables', month) : 0
  return delay({
    rows,
    budget,
    used,
    remaining: budget === null ? null : budget - used,
    original_snapshot: consumableSnapshots[`${centre}|${month}`] || [],
    frozen: isMonthFrozen(centre, month),
    status: cm.status || 'draft',
    po_number: cm.po_number || '',
    submitted_at: cm.submitted_at || null
  })
}

export const saveConsumableRoster = (data) => {
  const { centre, month, rows, status = 'draft' } = data
  if (isMonthFrozen(centre, month)) {
    return fail('This month has been frozen by HQ and can no longer be edited.')
  }
  const mKey = `${centre}|${month}`
  if (consumableMonths[mKey]?.status === 'submitted') {
    return fail(
      'This month has been submitted and is locked. Ask HQ to reopen it before editing.'
    )
  }
  // Overspend allowed — no hard block.
  const map = {}
  rows.forEach((r) => {
    if (Number(r.qty) > 0) {
      const id =
        r.consumable_item ||
        `adhoc-${String(r.name || 'item')
          .toLowerCase()
          .replace(/\s+/g, '-')}-${nextId('c')}`
      map[id] = {
        qty: Number(r.qty),
        unit_price: Number(r.unit_price),
        is_adhoc: !!r.is_adhoc,
        name: r.is_adhoc ? r.name || 'One-off item' : undefined,
        unit: r.is_adhoc ? r.unit || '' : undefined
      }
    }
  })
  consumableOrders[`${centre}|${month}`] = map
  // Record the submit lifecycle; submitting stamps a timestamp. The PO is
  // stamped by HQ at approval (see decideConsumables), not at submit.
  const prev = consumableMonths[mKey] || {}
  consumableMonths[mKey] = {
    status,
    po_number: prev.po_number || '',
    submitted_at:
      status === 'submitted'
        ? prev.submitted_at || new Date().toISOString()
        : prev.submitted_at || null
  }
  // Freeze the "original" list on the first save of the month.
  const snapKey = `${centre}|${month}`
  if (!consumableSnapshots[snapKey]) {
    consumableSnapshots[snapKey] = rows
      .filter((r) => Number(r.qty) > 0)
      .map((r) => ({
        name: r.is_adhoc ? r.name || 'One-off item' : undefined,
        consumable_item: r.is_adhoc ? null : r.consumable_item || null,
        unit: r.unit || '',
        qty: String(r.qty),
        unit_price: String(r.unit_price),
        total: String(Number(r.qty) * Number(r.unit_price))
      }))
  }
  return fetchConsumableRoster({ centre, month })
}

export const reopenConsumables = (centre, month) => {
  const mKey = `${centre}|${month}`
  const prev = consumableMonths[mKey] || {}
  consumableMonths[mKey] = { ...prev, status: 'draft', review_remarks: '' }
  return fetchConsumableRoster({ centre, month })
}

export const fetchConsumablesPending = () => delay({ results: [] })

export const decideConsumables = (centre, month, decision, remarks = '') => {
  const mKey = `${centre}|${month}`
  const prev = consumableMonths[mKey] || {}
  // HQ stamps the PO on approval (centres no longer get one at submit).
  const po_number =
    decision === 'approved'
      ? prev.po_number || `CON/C/${month.slice(0, 7)}/${nextId('po')}`
      : prev.po_number || ''
  consumableMonths[mKey] = {
    ...prev,
    status: decision,
    review_remarks: remarks,
    po_number
  }
  return delay({ centre, month, status: decision, review_remarks: remarks })
}

// ---- Catalogue (Program Material) ----
export const searchCatalogue = (query = '') => {
  const q = String(query).trim().toLowerCase()
  let rows
  if (!q) {
    rows = catalogue.slice(0, 12)
  } else {
    rows = catalogue
      .filter(
        (it) =>
          it.name.toLowerCase().includes(q) ||
          (it.reference_id || '').toLowerCase().includes(q)
      )
      .slice(0, 15)
  }
  return delay({ results: rows })
}

export const fetchCatalogueBrowse = (queries = {}) => {
  const q = String(queries.search || '').trim().toLowerCase()
  const limit = Number(queries.limit) || 50
  const offset = Number(queries.offset) || 0
  const filtered = q
    ? catalogue.filter(
        (it) =>
          it.name.toLowerCase().includes(q) ||
          (it.reference_id || '').toLowerCase().includes(q)
      )
    : catalogue
  return delay({
    results: filtered.slice(offset, offset + limit),
    count: filtered.length
  })
}

// ---- Program Material ----
export const fetchProgramMaterial = (queries = {}) => {
  const { centre, month } = queries
  const order = pmOrders[`${centre}|${month}`]
  const budget = budgetAmount(centre, 'program_material', month)
  const used = usedAmount(centre, 'program_material', month)
  return delay({
    status: order ? order.status : 'draft',
    lines: order ? order.lines : [],
    remarks: order ? order.remarks || '' : '',
    po_number: order ? order.po_number || '' : '',
    review_remarks: order ? order.review_remarks || '' : '',
    discrepancy: order ? order.discrepancy || false : false,
    discrepancy_notes: order ? order.discrepancy_notes || '' : '',
    original_snapshot: order ? order.original_snapshot || [] : [],
    centre_feedback: order ? order.centre_feedback || '' : '',
    frozen: isMonthFrozen(centre, month),
    attachments: order ? order.attachments || [] : [],
    budget,
    used,
    remaining: budget === null ? null : budget - used
  })
}

export const saveProgramMaterial = (data) => {
  const { centre, month, status, lines, remarks } = data
  if (isMonthFrozen(centre, month)) {
    return fail('This month has been frozen by HQ and can no longer be edited.')
  }
  // Overspend allowed — no hard block.
  const key = `${centre}|${month}`
  const existing = pmOrders[key] || {}
  // PO is stamped by HQ at approval (see decideOrder), not at centre submit.
  const poNumber = existing.po_number
  pmOrders[key] = {
    status,
    remarks: remarks || '',
    po_number: poNumber || '',
    // re-submitting after a rejection clears the prior decision remarks
    review_remarks: status === 'submitted' ? '' : existing.review_remarks || '',
    attachments: existing.attachments || [],
    discrepancy: existing.discrepancy || false,
    discrepancy_notes: existing.discrepancy_notes || '',
    original_snapshot: existing.original_snapshot || [],
    lines: lines.map((l) => ({
      ...l,
      uuid: l.uuid || nextId('pml'),
      line_status: l.line_status || 'pending',
      line_note: l.line_note || '',
      line_decision: l.line_decision || 'pending',
      decision_note: l.decision_note || '',
      line_total: Number(l.qty) * Number(l.unit_cost)
    }))
  }
  // Freeze the "original PO" the first time the order is submitted.
  if (status === 'submitted' && !pmOrders[key].original_snapshot.length) {
    pmOrders[key].original_snapshot = lines.map((l) => ({
      item_name: l.item_name,
      qty: String(l.qty),
      unit_cost: String(l.unit_cost),
      line_total: String(Number(l.qty) * Number(l.unit_cost)),
      reference_id: l.reference_id || '',
      unit: l.unit || '',
      remarks: l.remarks || ''
    }))
  }
  return fetchProgramMaterial({ centre, month })
}

// ---- Workflow: approvals, attachments, notifications ----
export const fetchAllOrders = () => {
  const rows = Object.entries(pmOrders)
    .filter(([, o]) => o.status && o.status !== 'draft')
    .map(([k, o]) => {
      const [centre, month] = k.split('|')
      return {
        centre,
        centreName: centreName(centre),
        month,
        status: o.status,
        po_number: o.po_number || '',
        review_remarks: o.review_remarks || '',
        discrepancy: o.discrepancy || false,
        discrepancy_notes: o.discrepancy_notes || '',
        items: o.lines.length,
        total: o.lines.reduce(
          (s, l) =>
            l.line_decision === 'rejected'
              ? s
              : s + Number(l.qty) * Number(l.unit_cost),
          0
        ),
        attachments: (o.attachments || []).length
      }
    })
    .sort((a, b) => b.month.localeCompare(a.month))
  return delay({ results: rows })
}

export const decideOrder = (centre, month, decision, remarks = '') => {
  const o = pmOrders[`${centre}|${month}`]
  if (!o) return fail('Order not found')
  o.status = decision
  o.review_remarks = remarks
  // HQ stamps the PO when it first takes a positive decision (not at submit).
  if (decision !== 'rejected' && !o.po_number) {
    o.po_number = generatePo(centre, month)
  }
  // On approval, pending lines become accepted; rejected stay rejected.
  if (decision === 'approved') {
    o.lines = o.lines.map((l) => ({
      ...l,
      line_decision: l.line_decision === 'rejected' ? 'rejected' : 'accepted'
    }))
  }
  pushNotification({
    title: `Order ${decision}`,
    link: `program-material:${centre}:${month}`,
    body: `${o.po_number || 'Program Material'} (${centreName(centre)}, ${month.slice(
      0,
      7
    )}) was ${decision}${remarks ? ': ' + remarks : ''}`,
    centre,
    month
  })
  return delay({ ...o })
}

export const setOrderLineDecisions = (centre, month, { lines = [], all } = {}) => {
  const o = pmOrders[`${centre}|${month}`]
  if (!o) return fail('Order not found')
  const valid = ['pending', 'accepted', 'rejected']
  if (valid.includes(all)) {
    o.lines = o.lines.map((l) => ({ ...l, line_decision: all }))
  }
  const byUuid = {}
  lines.forEach((u) => {
    byUuid[u.uuid] = u
  })
  o.lines = o.lines.map((l) => {
    const u = byUuid[l.uuid]
    if (!u) return l
    return {
      ...l,
      line_decision: valid.includes(u.line_decision)
        ? u.line_decision
        : l.line_decision,
      decision_note:
        u.decision_note !== undefined ? u.decision_note : l.decision_note
    }
  })
  return fetchProgramMaterial({ centre, month })
}

export const saveOrderFeedback = (centre, month, feedback) => {
  const o = pmOrders[`${centre}|${month}`]
  if (!o) return fail('Order not found')
  o.centre_feedback = feedback || ''
  return fetchProgramMaterial({ centre, month })
}

export const orderAdminAction = (centre, month, action) => {
  const key = `${centre}|${month}`
  const o = pmOrders[key]
  if (!o) return fail('Order not found')
  if (action === 'delete') {
    delete pmOrders[key]
    return delay({ ok: true, deleted: true })
  }
  // reopen -> centre can edit again
  o.status = 'draft'
  o.review_remarks = ''
  return fetchProgramMaterial({ centre, month })
}

export const addPmAttachment = (centre, month, fileName) => {
  const o = pmOrders[`${centre}|${month}`]
  if (!o) return fail('Save the order first')
  o.attachments = o.attachments || []
  o.attachments.push({ name: fileName })
  return delay({ results: o.attachments })
}

// In-memory attachment store. scope is {centre, month} or {entry}.
const mockAttachments = {}
const scopeKey = (entityType, scope = {}) =>
  `${entityType}|${scope.entry || `${scope.centre}|${scope.month}`}`

export const uploadAttachment = (entityType, scope, file) => {
  const key = scopeKey(entityType, scope)
  mockAttachments[key] = mockAttachments[key] || []
  mockAttachments[key].push({
    uuid: `${key}|${mockAttachments[key].length}`,
    name: file.name,
    url: '#mock-no-download'
  })
  return delay({ results: mockAttachments[key] })
}

export const fetchAttachments = (entityType, scope) => {
  const key = scopeKey(entityType, scope)
  return delay({ results: mockAttachments[key] || [] })
}

export const uploadItemImage = (file) =>
  delay({ key: `mock/${file.name}`, url: '#mock-no-download' })

export const fetchNotifications = () =>
  delay({
    results: notifications,
    unread: notifications.filter((n) => !n.read).length
  })

export const markNotificationsRead = () => {
  notifications = notifications.map((n) => ({ ...n, read: true }))
  return delay({ ok: true })
}

// ---- SOP (per-category "how to use" text) ----
let sops = Object.entries(DEFAULT_SOPS).map(([category, content]) => ({
  category,
  content
}))

export const fetchSops = () => delay({ results: sops })

export const updateSop = ({ category, content }) => {
  sops = sops.some((s) => s.category === category)
    ? sops.map((s) => (s.category === category ? { ...s, content } : s))
    : [...sops, { category, content }]
  return delay({ category, content })
}

// ---- History (View / Edit support) ----
export const fetchProgramMaterialHistory = (queries = {}) => {
  const { centre } = queries
  const rows = Object.entries(pmOrders)
    .filter(([k]) => k.startsWith(`${centre}|`))
    .map(([k, o]) => {
      const month = k.split('|')[1]
      const total = o.lines.reduce(
        (s, l) => s + Number(l.qty) * Number(l.unit_cost),
        0
      )
      return {
        month,
        status: o.status,
        po_number: o.po_number || '',
        review_remarks: o.review_remarks || '',
        items: o.lines.length,
        total
      }
    })
    .filter((r) => r.items > 0)
    .sort((a, b) => b.month.localeCompare(a.month))
  return delay({ results: rows })
}

export const fetchConsumablesHistory = (queries = {}) => {
  const { centre } = queries
  const rows = Object.entries(consumableOrders)
    .filter(([k]) => k.startsWith(`${centre}|`))
    .map(([k, map]) => {
      const month = k.split('|')[1]
      const items = Object.keys(map).length
      const total = Object.values(map).reduce(
        (s, r) => s + Number(r.qty) * Number(r.unit_price),
        0
      )
      return { month, items, total }
    })
    .filter((r) => r.items > 0)
    .sort((a, b) => b.month.localeCompare(a.month))
  return delay({ results: rows })
}

export const updatePettyCash = (uuid, data) => {
  pettyCash = pettyCash.map((e) => (e.uuid === uuid ? { ...e, ...data } : e))
  return delay(pettyCash.find((e) => e.uuid === uuid))
}

export const confirmReceipt = (centre, month, lines = []) => {
  const o = pmOrders[`${centre}|${month}`]
  if (!o) return fail('Order not found')
  if (o.status !== 'sent') {
    return fail('The order must be marked sent before you can confirm receipt.')
  }
  const byUuid = {}
  lines.forEach((l) => {
    byUuid[l.uuid] = l
  })
  ;(o.lines || []).forEach((ln) => {
    const upd = byUuid[ln.uuid]
    if (upd) {
      ln.receipt_status = upd.receipt_status || 'received'
      ln.receipt_note =
        upd.receipt_status === 'discrepancy' ? upd.receipt_note || '' : ''
    }
  })
  const discLines = (o.lines || []).filter(
    (ln) => ln.receipt_status === 'discrepancy'
  )
  const missingLines = (o.lines || []).filter(
    (ln) => ln.receipt_status === 'not_received'
  )
  o.status = 'received'
  o.discrepancy = discLines.length > 0 || missingLines.length > 0
  o.discrepancy_notes = discLines
    .map((ln) => `${ln.item_name}: ${ln.receipt_note}`)
    .concat(missingLines.map((ln) => `${ln.item_name}: not received`))
    .join('; ')
  pushNotification({
    title: 'Order received' + (o.discrepancy ? ' with issues' : ''),
    body: `${o.po_number || 'Program Material'} (${centreName(centre)}, ${month.slice(
      0,
      7
    )}) was received`,
    centre,
    month
  })
  return fetchProgramMaterial({ centre, month })
}

export const setOrderLineStatuses = (centre, month, lines = [], markSent = false) => {
  const o = pmOrders[`${centre}|${month}`]
  if (!o) return fail('Order not found')
  const byUuid = {}
  ;(lines || []).forEach((u) => {
    byUuid[u.uuid] = u
  })
  o.lines = o.lines.map((l) => {
    const u = byUuid[l.uuid]
    if (!u) return l
    return {
      ...l,
      line_status: ['pending', 'sent', 'unavailable'].includes(u.line_status)
        ? u.line_status
        : l.line_status,
      line_note: u.line_note || ''
    }
  })
  if (markSent) {
    if (o.status !== 'approved') return fail('Only an approved order can be marked sent.')
    o.status = 'sent'
    pushNotification({
      title: 'Order sent',
      body: `${o.po_number || 'Program Material'} (${centreName(centre)}, ${month.slice(
        0,
        7
      )}) was sent`,
      centre,
      month
    })
  }
  return fetchProgramMaterial({ centre, month })
}
